import { useMutation } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";

import { Button, IconButton, Modal } from "../../../../../../../components/ui";
import { REMOVE_INVOICE_ITEM as removeInvoiceItemMutation } from "../../../../../../../graphql/mutation/Invoice";
import { hasCreditNoteAgainstSale } from "./saleDeletion";

const Actions = props => {
  const { item, invoiceId, invoiceItems } = props;
  const { itemId, disabled, isExported, isCreditNote, isCreditedSale } =
    useMemo(() => {
      const isExported = item?.values?.exported !== 0;
      const isCreditNote = item?.values?.typeId === 6;
      const isCreditedSale = hasCreditNoteAgainstSale({
        invoiceItems,
        sale: item?.values,
      });

      return {
        itemId: item?.id,
        disabled: isExported || isCreditNote || isCreditedSale,
        isExported,
        isCreditNote,
        isCreditedSale,
      };
    }, [invoiceItems, item]);

  const [removeInvoiceItem, { loading, error, reset }] = useMutation(
    removeInvoiceItemMutation({
      instance: { edition: { artwork: { imagesSummary: { imgT: {} } } } },
      type: {},
    }),
    { refetchQueries: ["FetchItemsV2", "FetchInvoice"] },
  );

  const deleteModalProps = {
    title: "Delete Transaction",
    description: error ? "" : "Are you sure?",
    scale: "sm",
    disabled,
    closeOnBackdrop: false,
    renderBody: ({ closeModal }) => {
      const handleDelete = async () => {
        const response = await removeInvoiceItem({
          variables: {
            input: {
              id: invoiceId,
              itemId,
            },
          },
        });

        if (response?.data?.removeInvoiceItem?.success) {
          closeModal();
        }
      };

      if (error) {
        return (
          <>
            <div className="mt-3 inline-flex items-center justify-start rounded-md border border-transparent px-4 py-2 font-bold font-medium text-red-800 sm:text-sm">
              {error?.message ||
                error?.graphQLErrors?.map?.(({ message }) => `${message} `) ||
                "An error occurred"}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                className="mr-3"
                label="Close"
                onClick={() => {
                  reset();
                  closeModal();
                }}
              />
            </div>
          </>
        );
      }

      return (
        <div className="mt-4 flex justify-end">
          <Button
            className="mr-3"
            label="Cancel"
            onClick={() => {
              closeModal();
            }}
          />
          <Button
            label={loading ? "Deleting" : "Delete"}
            disabled={loading}
            onClick={handleDelete}
          />
        </div>
      );
    },
  };

  if (!itemId) {
    return null;
  }

  // Exported lines can't be deleted: in place of the trash button they show a
  // tiny EXPORTED pill so it's clear why no delete action is offered.
  if (isExported) {
    return (
      <div className="user-list-actions flex justify-end">
        <span className="inline-flex items-center whitespace-nowrap rounded-full border border-green-700 bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-green-800">
          Exported
        </span>
      </div>
    );
  }

  // Credit-note lines also can't be deleted, so the button is shown but turned
  // off: the Modal's `disabled` guard stops it opening and the icon is greyed /
  // non-interactive.
  const deleteTitle = isCreditNote
    ? "Credit note transactions can't be deleted"
    : isCreditedSale
      ? "Sales with a credit note against them can't be deleted"
      : "Delete Transaction";

  return (
    <div className="user-list-actions flex text-right text-sm font-medium">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Modal {...deleteModalProps}>
          <IconButton variant="clean" title={deleteTitle} disabled={disabled}>
            <TrashIcon
              className={`h-4 w-4 text-gray-400 ${disabled ? "cursor-default opacity-40" : "cursor-pointer"}`}
            />
          </IconButton>
        </Modal>
      </div>
    </div>
  );
};

export default Actions;
